/**
 * C-GTCC-VIEW-HW-GW2 — the panels of the GTCC view "Hardware & SIMs".
 *
 * One component per drawn element of `GTCC_REFERENCE_2026-09-01.html:736-770`,
 * so `HwView.tsx` keeps the data and the two decisions and nothing else:
 *
 *   - {@link HwApprovalQueuePanel} — panel "Freigabe-Warteschlange" with the
 *     reference's five columns IMEI | ERSTKONTAKT | MODELL | VORGESCHLAGENER
 *     TENANT | AKTION and its `[Freigeben]` / `[Ablehnen]` cell,
 *   - {@link HwTiles} — the four `.card`s of the `.tiles` strip,
 *   - {@link HwFlaggedPanel} — panel "Auffällige Geräte",
 *   - {@link HwStockPanel} — panel "Managed Hardware (Lager)",
 *   - {@link HwRowsPanel} — the measured rows of an envelope, used by the tabs
 *     "Inventar" and "SIMs",
 *   - {@link HwPoolPanel} — the pool list behind the tab "Managed Hardware".
 *
 * Three properties hold everywhere in here, and they are the reason several of
 * these cells look more cautious than the reference's sample text:
 *   - no number is computed from another number, and none is invented: a
 *     missing measurement renders {@link ControlNotMeasurable} plus the API's
 *     own `reason`, never a zero (P8),
 *   - no age is derived from the local clock — `ageSeconds` arrives decided by
 *     the database clock, and an absolute timestamp is printed as such (P9),
 *   - a `null` that means "not readable here" is never printed as "none": the
 *     FAHRZEUG column says why it is empty (`vehicleReason`).
 */
import { EmptyState, Stack } from "@genius-tracker/design-system";
import type { Locale } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import type { CSSProperties, ReactNode } from "react";

import { formatTimestampSafe } from "../../../../locale-format.js";
import {
  type HwFlaggedDeviceDto,
  type HwFlaggedDevicesDto,
  type HwInventoryDto,
  type HwPendingDeviceDto,
  type HwPoolDto,
  type HwRowDto,
  type HwVehicleFlowDto,
} from "../../../api/hw.js";
import {
  ControlMono,
  ControlNotMeasurable,
  ControlNote,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  ControlTextAction,
  ControlTile,
  ControlTiles,
  HOST_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  asList,
  formatInteger,
  maskImei,
  measuredNumber,
  toneOfVerdict,
  type ControlTone,
} from "../../control/ControlPrimitives.js";

/* ------------------------------------------------------------------ tabs */

/**
 * The reference draws four `.tab`s at `font-size:12px`, the first one active.
 * The ids are the URL values of `?tab=`, so they are part of the deep link and
 * must not be renamed silently.
 */
export const HW_TAB_IDS = ["approvals", "inventory", "sims", "managed"] as const;
export type HwTabId = (typeof HW_TAB_IDS)[number];

export const HW_STALE_MS = 30_000;
export const HW_REFETCH_MS = 60_000;

export const HW_QUERY_OPTIONS = {
  staleTime: HW_STALE_MS,
  refetchInterval: HW_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

/** `.tabs` of the reference sits one step above the panels at 12 px. */
export const TAB_BAR_STYLE: CSSProperties = { fontSize: "var(--gt-sys-font-size-xs)" };

export const HW_SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/**
 * A caption every table carries and no reader sees twice: the reference prints
 * the table's name in the panel heading above it, so repeating it visibly would
 * be a duplicate — while a table without a name is unreachable for anyone
 * navigating by table (`gtcc-table-semantics`).
 */
const CAPTION_STYLE: CSSProperties = {
  captionSide: "top",
  textAlign: "start",
  display: "none",
};

const ACTION_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  whiteSpace: "nowrap",
};

const ACTION_GROUP_STYLE: CSSProperties = {
  display: "flex",
  gap: "var(--gt-sys-space-2)",
};

/* --------------------------------------------- 1 · Freigabe-Warteschlange */

export interface HwQueueProps {
  readonly pending: ReadonlyArray<HwPendingDeviceDto>;
  /** `truncated` of `HwPendingDevicesDto` — a full page is not a full queue. */
  readonly truncated: boolean;
  readonly locale: Locale;
  /** The device a decision is currently in flight for, or `null`. */
  readonly busyId: string | null;
  readonly onApprove: (device: HwPendingDeviceDto) => void;
  readonly onReject: (device: HwPendingDeviceDto) => void;
}

/**
 * The reference's queue, with its promise in the heading: "neue Geräte melden
 * sich — nichts sendet ohne Freigabe".
 *
 * ERSTKONTAKT prints `createdAt` as an absolute time in a `<time>` element. An
 * age ("heute 14:22" of the reference is one) would have to come from this
 * browser's clock, and a clock that is wrong would make an old device look new.
 *
 * A row whose suggested tenant is unknown cannot be approved: the approve route
 * decides *into* a tenant, so there is nothing to decide. Such a row keeps both
 * actions disabled and says why, rather than offering a verb that would fail.
 *
 * E-GTCC-VIEW-DPLANE-GW-HW1, Ursache 2 (Zeilenraster): die AKTION-Zelle stand
 * mit zwei `Button`-Elementen des Design-Systems. Deren Rahmen, Polsterung und
 * Mindesthoehe haben die Zeile auf 47 px getrieben, wo die Referenz 32 px setzt
 * (gemessen mit `getBoundingClientRect`, hell, 1600x900). Die Zelle nimmt
 * deshalb `ControlTextAction` mit `brackets` — genau die Text-Variante, die
 * `ControlPrimitives.tsx` fuer diesen Fall haelt und die die Referenz in
 * Zeile 744 als `[Freigeben] [Ablehnen]` schreibt. Kein lokaler Wert.
 */
export function HwApprovalQueuePanel(props: HwQueueProps): ReactNode {
  const { t, locale } = useLocale();
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <ControlPanel
      testId="gtcc-hw-queue"
      title={t("admin.gtcc.hw.queue.title")}
      note={t("admin.gtcc.hw.queue.note")}
    >
      {props.pending.length === 0 ? (
        <div data-testid="gtcc-hw-queue-empty">
          <EmptyState
            title={t("admin.gtcc.hw.queue.empty.title")}
            body={t("admin.gtcc.hw.queue.empty.body")}
          />
        </div>
      ) : (
        <ControlTableWrap>
          <table style={TABLE_STYLE} data-testid="gtcc-hw-queue-table">
            <caption style={CAPTION_STYLE}>{t("admin.gtcc.hw.queue.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.queue.col.imei")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.queue.col.first_contact")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.queue.col.model")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.queue.col.tenant")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.queue.col.action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {props.pending.map((device) => {
                const decidable = device.tenantId !== null;
                const busy = props.busyId === device.id;
                return (
                  <tr key={device.id} data-testid="gtcc-hw-queue-row">
                    <td style={HOST_CELL_STYLE}>{maskImei(device.imei)}</td>
                    <td style={TD_STYLE}>
                      <time dateTime={device.createdAt}>
                        {formatTimestampSafe(device.createdAt, (date) =>
                          dateFormatter.format(date),
                        )}
                      </time>
                    </td>
                    <td style={TD_STYLE}>{device.deviceType}</td>
                    <td style={TD_STYLE}>
                      {decidable ? (
                        <ControlMono>
                          {device.tenantDisplayName ?? device.tenantSlug ?? device.tenantId}
                        </ControlMono>
                      ) : (
                        <ControlNotMeasurable
                          label={t("admin.gtcc.hw.queue.no_tenant")}
                          testId="gtcc-hw-queue-no-tenant"
                        />
                      )}
                    </td>
                    <td style={ACTION_CELL_STYLE}>
                      <div style={ACTION_GROUP_STYLE}>
                        <ControlTextAction
                          label={t("admin.gtcc.hw.queue.approve")}
                          brackets
                          disabled={!decidable || busy}
                          onActivate={() => props.onApprove(device)}
                          testId="gtcc-hw-queue-approve"
                        />
                        <ControlTextAction
                          label={t("admin.gtcc.hw.queue.reject")}
                          brackets
                          disabled={!decidable || busy}
                          onActivate={() => props.onReject(device)}
                          testId="gtcc-hw-queue-reject"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ControlTableWrap>
      )}
      {props.truncated ? (
        <ControlNote testId="gtcc-hw-queue-truncated">
          {t("admin.gtcc.hw.queue.truncated", {
            shown: formatInteger(props.pending.length, props.locale),
          })}
        </ControlNote>
      ) : null}
    </ControlPanel>
  );
}

/* ------------------------------------------------------------- 2 · tiles */

export interface HwTilesProps {
  readonly flow: HwVehicleFlowDto | null;
  readonly inventory: HwInventoryDto | null;
  /** `null` when the queue itself could not be read — never a substituted 0. */
  readonly pendingCount: number | null;
  readonly locale: Locale;
}

function tileValue(value: number | null, locale: Locale, unmeasured: string): ReactNode {
  return value === null ? (
    <ControlNotMeasurable label={unmeasured} />
  ) : (
    formatInteger(value, locale)
  );
}

/**
 * The four `.card`s of the reference: TRACKER GESAMT, STILL > 24 H, WARTEN AUF
 * FREIGABE, SIM AKTIV.
 *
 * Two status lines are deliberately narrower than the reference's:
 *   - the reference writes "16 URBAGEC" under STILL > 24 H, which is a silent
 *     count per tenant. The vehicle-flow projection measures the LARGEST tenant
 *     by fleet, not the silent ones, so the line names exactly that and labels
 *     it as such — the reference's number has no source here,
 *   - "1 nahe Datenlimit" needs consumed SIM volume, which no column carries;
 *     that line therefore reads "nicht messbar".
 */
export function HwTiles(props: HwTilesProps): ReactNode {
  const { t } = useLocale();
  const unmeasured = t("admin.gtcc.hw.not_measurable");
  const locale = props.locale;

  const total = measuredNumber(props.flow?.total);
  const sending = measuredNumber(props.flow?.sending);
  const silent = measuredNumber(props.flow?.silent24h);
  const largestName = props.flow?.largestTenant?.name ?? null;
  const largestTotal = measuredNumber(props.flow?.largestTenant?.total);
  const simActive = measuredNumber(props.inventory?.simActive);
  const nearLimit = measuredNumber(props.inventory?.simNearDataLimit);

  return (
    <ControlTiles testId="gtcc-hw-tiles">
      <ControlTile
        testId="gtcc-hw-tile-trackers"
        label={t("admin.gtcc.hw.tiles.tracker_total")}
        value={tileValue(total, locale, unmeasured)}
        tone={total === null ? "muted" : "ok"}
        statusTone={sending === null ? "muted" : "ok"}
        statusText={
          sending === null
            ? unmeasured
            : t("admin.gtcc.hw.tiles.tracker_sending", {
                sending: formatInteger(sending, locale),
              })
        }
      />
      <ControlTile
        testId="gtcc-hw-tile-silent"
        label={t("admin.gtcc.hw.tiles.silent")}
        value={tileValue(silent, locale, unmeasured)}
        tone={silent === null ? "muted" : silent > 0 ? "warn" : "ok"}
        statusTone={largestName === null || largestTotal === null ? "muted" : "info"}
        statusText={
          largestName === null || largestTotal === null
            ? unmeasured
            : t("admin.gtcc.hw.tiles.largest_tenant", {
                tenant: largestName,
                total: formatInteger(largestTotal, locale),
              })
        }
      />
      <ControlTile
        testId="gtcc-hw-tile-pending"
        label={t("admin.gtcc.hw.tiles.pending")}
        value={tileValue(props.pendingCount, locale, unmeasured)}
        tone={props.pendingCount === null ? "muted" : props.pendingCount > 0 ? "info" : "ok"}
        statusTone="info"
        statusText={t("admin.gtcc.hw.tiles.pending_status")}
      />
      <ControlTile
        testId="gtcc-hw-tile-sim"
        label={t("admin.gtcc.hw.tiles.sim_active")}
        value={tileValue(simActive, locale, unmeasured)}
        tone={simActive === null ? "muted" : "ok"}
        statusTone={nearLimit === null ? "muted" : nearLimit > 0 ? "warn" : "ok"}
        statusText={
          nearLimit === null
            ? t("admin.gtcc.hw.tiles.sim_near_limit_unmeasurable")
            : t("admin.gtcc.hw.tiles.sim_near_limit", {
                count: formatInteger(nearLimit, locale),
              })
        }
      />
    </ControlTiles>
  );
}

/* --------------------------------------------------- 3 · Auffällige Geräte */

function AgeCell(props: { ageSeconds: number | null; locale: Locale }): ReactNode {
  const { t } = useLocale();
  const age = measuredNumber(props.ageSeconds);
  if (age === null) {
    return <ControlNotMeasurable label={t("admin.gtcc.hw.not_measurable")} />;
  }
  return (
    <ControlMono>
      {t("admin.gtcc.hw.flagged.age", { seconds: formatInteger(age, props.locale) })}
    </ControlMono>
  );
}

function VehicleCell(props: { device: HwFlaggedDeviceDto }): ReactNode {
  const { t } = useLocale();
  const { vehicleLabel, vehicleReason } = props.device;
  if (vehicleLabel !== null) return <ControlMono>{vehicleLabel}</ControlMono>;
  return (
    <ControlNotMeasurable
      testId="gtcc-hw-flagged-no-vehicle"
      label={
        vehicleReason === null
          ? t("admin.gtcc.hw.not_measurable")
          : t(`admin.gtcc.hw.reason.${vehicleReason}`)
      }
    />
  );
}

/**
 * The reference's second panel: the devices that ask for a look.
 *
 * The rows are the cross-tenant rejection window of the API, so PROBLEM is the
 * log's own reason code and ZULETZT is the API's `ageSeconds`. Whether the
 * window was longer than the projection returns is stated (`truncated`) rather
 * than left to look like "that is all there was". PROBLEM's reason code is
 * translated via `admin.gtcc.hw.flagged.problem.<code>` (D-GTCC-VIEW-TRIAGE1
 * hw-1) — an unmapped code still renders (as the raw code) rather than
 * disappearing, since a missing catalog key falls back to the key itself.
 * The panel carries no heading note: the reference draws the table right
 * under the title with nothing beside it (D-GTCC-VIEW-TRIAGE1 hw-7).
 */
export function HwFlaggedPanel(props: {
  flagged: HwFlaggedDevicesDto | null;
  locale: Locale;
}): ReactNode {
  const { t } = useLocale();
  const devices = asList(props.flagged?.devices);

  return (
    <ControlPanel testId="gtcc-hw-flagged" title={t("admin.gtcc.hw.flagged.title")}>
      {devices.length === 0 ? (
        <div data-testid="gtcc-hw-flagged-empty">
          <EmptyState
            title={t("admin.gtcc.hw.flagged.empty.title")}
            body={t("admin.gtcc.hw.flagged.empty.body")}
          />
        </div>
      ) : (
        <ControlTableWrap>
          <table style={TABLE_STYLE} data-testid="gtcc-hw-flagged-table">
            <caption style={CAPTION_STYLE}>{t("admin.gtcc.hw.flagged.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.flagged.col.imei")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.flagged.col.tenant")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.flagged.col.vehicle")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.flagged.col.last")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.flagged.col.problem")}
                </th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr
                  key={device.key}
                  data-testid="gtcc-hw-flagged-row"
                  data-tone={toneOfVerdict(device.verdict)}
                >
                  <td style={HOST_CELL_STYLE}>{maskImei(device.imei)}</td>
                  <td style={TD_STYLE}>
                    {device.tenantName === null ? (
                      <ControlNotMeasurable label={t("admin.gtcc.hw.not_measurable")} />
                    ) : (
                      device.tenantName
                    )}
                  </td>
                  <td style={TD_STYLE}>
                    <VehicleCell device={device} />
                  </td>
                  <td style={TD_STYLE}>
                    <AgeCell ageSeconds={device.ageSeconds} locale={props.locale} />
                  </td>
                  <td style={TD_STYLE}>
                    <ControlMono tone={toneOfVerdict(device.verdict)}>
                      {t(`admin.gtcc.hw.flagged.problem.${device.problem}`)}
                    </ControlMono>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ControlTableWrap>
      )}
      {props.flagged?.truncated === true ? (
        <ControlNote testId="gtcc-hw-flagged-truncated">
          {t("admin.gtcc.hw.flagged.truncated", {
            shown: formatInteger(devices.length, props.locale),
          })}
        </ControlNote>
      ) : null}
    </ControlPanel>
  );
}

/* ---------------------------------------- 4 · Managed Hardware (Lager) */

/**
 * The reference's three stock rows. Only the first has a source: the pool list
 * is the devices that exist without a tenant, which is exactly "Auf Lager,
 * unverbaut". "Bestellt, unterwegs" and "RMA / defekt" are procurement states
 * the schema does not model, so they arrive as unmeasurable rows of the
 * inventory projection and are printed as such — the reference's `40
 * (Bestell-Nr. 2291)` has nowhere to come from.
 *
 * The pool route caps its page; a full page means there are more, and that is
 * said instead of printing the cap as if it were the count.
 */
export function HwStockPanel(props: {
  pool: HwPoolDto | null;
  inventory: HwInventoryDto | null;
  locale: Locale;
}): ReactNode {
  const { t } = useLocale();
  const items = asList(props.pool?.items);
  const capped = props.pool?.truncated === true;
  const stockRows = asList(props.inventory?.rows).filter((row) => row.key.startsWith("stock_"));

  return (
    <ControlPanel testId="gtcc-hw-stock" title={t("admin.gtcc.hw.stock.title")}>
      <ControlRow
        testId="gtcc-hw-stock-in-stock"
        tone={props.pool === null ? "muted" : "ok"}
        label={t("admin.gtcc.hw.stock.in_stock")}
        value={
          props.pool === null ? (
            <ControlNotMeasurable label={t("admin.gtcc.hw.not_measurable")} />
          ) : (
            <ControlMono>
              {t("admin.gtcc.hw.stock.in_stock_value", {
                count: formatInteger(items.length, props.locale),
              })}
            </ControlMono>
          )
        }
      />
      {stockRows.map((row) => (
        <HwRow key={row.key} row={row} locale={props.locale} />
      ))}
      {capped ? (
        <ControlNote testId="gtcc-hw-stock-truncated">
          {t("admin.gtcc.hw.stock.truncated", {
            shown: formatInteger(items.length, props.locale),
          })}
        </ControlNote>
      ) : null}
    </ControlPanel>
  );
}

/* --------------------------------------------------- 5 · measured rows */

/**
 * Row labels come from this catalog, not from the payload: the API's `label` is
 * one German string for every reader, and this deck answers in four languages.
 * A key the catalog does not know falls back to the payload's own label, which
 * is worse than a translation and better than an empty cell.
 */
const HW_ROW_LABEL_KEYS: Readonly<Record<string, string>> = {
  flagged_devices: "admin.gtcc.hw.row.flagged_devices",
  sim_total: "admin.gtcc.hw.row.sim_total",
  sim_active: "admin.gtcc.hw.row.sim_active",
  sim_near_data_limit: "admin.gtcc.hw.row.sim_near_data_limit",
  stock_ordered: "admin.gtcc.hw.row.stock_ordered",
  stock_rma: "admin.gtcc.hw.row.stock_rma",
  sending_15m: "admin.gtcc.hw.row.sending_15m",
  silent_24h: "admin.gtcc.hw.row.silent_24h",
  positions_per_min: "admin.gtcc.hw.row.positions_per_min",
  largest_tenant: "admin.gtcc.hw.row.largest_tenant",
};

/** One measured row of an envelope, with its verdict as the row's tone. */
export function HwRow(props: { row: HwRowDto; locale: Locale }): ReactNode {
  const { t } = useLocale();
  const { row } = props;
  const labelKey = HW_ROW_LABEL_KEYS[row.key];
  const tone: ControlTone = toneOfVerdict(row.verdict);
  const unmeasurable = row.verdict === "unmeasurable";

  return (
    <ControlRow
      testId={`gtcc-hw-row-${row.key}`}
      tone={tone}
      label={labelKey === undefined ? row.label : t(labelKey)}
      value={
        unmeasurable ? (
          <ControlNotMeasurable
            label={
              row.reason === undefined
                ? t("admin.gtcc.hw.not_measurable")
                : t(`admin.gtcc.hw.reason.${row.reason}`)
            }
          />
        ) : (
          <ControlMono tone={tone}>{row.valueText}</ControlMono>
        )
      }
    />
  );
}

/** The rows of one envelope as a panel — the body of the tabs 2 and 3. */
export function HwRowsPanel(props: {
  title: string;
  note?: string;
  testId: string;
  rows: ReadonlyArray<HwRowDto>;
  locale: Locale;
  emptyTitle: string;
  emptyBody: string;
}): ReactNode {
  return (
    <ControlPanel
      testId={props.testId}
      title={props.title}
      {...(props.note === undefined ? {} : { note: props.note })}
    >
      {props.rows.length === 0 ? (
        <div data-testid={`${props.testId}-empty`}>
          <EmptyState title={props.emptyTitle} body={props.emptyBody} />
        </div>
      ) : (
        <Stack gap={0}>
          {props.rows.map((row) => (
            <HwRow key={row.key} row={row} locale={props.locale} />
          ))}
        </Stack>
      )}
    </ControlPanel>
  );
}

/* --------------------------------------------------- 6 · Managed Hardware */

/** The pool list itself: the devices the platform holds without a tenant. */
export function HwPoolPanel(props: { pool: HwPoolDto | null; locale: Locale }): ReactNode {
  const { t, locale } = useLocale();
  const items = asList(props.pool?.items);
  const dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });

  return (
    <ControlPanel
      testId="gtcc-hw-pool"
      title={t("admin.gtcc.hw.pool.title")}
      note={t("admin.gtcc.hw.pool.note", { count: formatInteger(items.length, props.locale) })}
    >
      {items.length === 0 ? (
        <div data-testid="gtcc-hw-pool-empty">
          <EmptyState
            title={t("admin.gtcc.hw.pool.empty.title")}
            body={t("admin.gtcc.hw.pool.empty.body")}
          />
        </div>
      ) : (
        <ControlTableWrap>
          <table style={TABLE_STYLE} data-testid="gtcc-hw-pool-table">
            <caption style={CAPTION_STYLE}>{t("admin.gtcc.hw.pool.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.pool.col.imei")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.pool.col.model")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.pool.col.status")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.hw.pool.col.since")}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((device) => (
                <tr key={device.id} data-testid="gtcc-hw-pool-row">
                  <td style={HOST_CELL_STYLE}>{device.imei}</td>
                  <td style={TD_STYLE}>{device.deviceType}</td>
                  <td style={TD_STYLE}>
                    <ControlMono>{device.status}</ControlMono>
                  </td>
                  <td style={TD_STYLE}>
                    <time dateTime={device.createdAt}>
                      {formatTimestampSafe(device.createdAt, (date) => dateFormatter.format(date))}
                    </time>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ControlTableWrap>
      )}
      {props.pool?.truncated === true ? (
        <ControlNote testId="gtcc-hw-pool-truncated">
          {t("admin.gtcc.hw.stock.truncated", {
            shown: formatInteger(items.length, props.locale),
          })}
        </ControlNote>
      ) : null}
    </ControlPanel>
  );
}
